import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// --- Core Imports ---
import { useAuth } from "../../contexts/AuthContext";

// --- Component Imports ---
import axiosInstance from "../../axiosInstance";
import CronJobForm from "./CronJobForm";
import EventForm from "./EventForm";
import ToggleButton from "../../components/ToggleButton/ToggleButton";
import IconButton from "../../components/IconButton/IconButton";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import GeneralTable from "../../components/Table/GeneralTable";
import UpgradePlan from "../UpgradePlan";

// --- Authorization Config ---
import { EMAIL_AUTOMATION_AUTH } from "../../config/authorizationConfig";

// --- Styles ---
import "./EmailAutomation.scss";

const EmailAutomation = () => {
  const { user, isLoading: isAuthLoading, isAuthorized } = useAuth();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [cronjobTypes, setCronjobTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCronJobFormOpen, setIsCronJobFormOpen] = useState(false);
  const [editingCronJobId, setEditingCronJobId] = useState(null);
  const [isEventJobFormOpen, setIsEventJobFormOpen] = useState(false);
  const [eventJobId, setEventJobId] = useState(null);
  const [ifHeading, setHeading] = useState(false);

  // Check if user has access to Email Automation
  const hasEmailAutomationAccess = () => {
    // Check using the EMAIL_AUTOMATION_AUTH.VIEW configuration
    return isAuthorized(EMAIL_AUTOMATION_AUTH.VIEW);
  };

  useEffect(() => {
    // We only fetch data if the user object is available and has access
    if (user && hasEmailAutomationAccess()) {
      fetchCategories();
    }
  }, [user]);

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/automation-category/all`,
      );
      if (data.data?.length > 0) {
        setCategories(data.data);
        const firstCategory = data.data[0];
        setSelectedCategory(firstCategory);
        fetchCategoryDetails(firstCategory.categoryId);
      } else {
        setHeading(true);
      }
    } catch (err) {
      setError("Failed to load categories.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryDetails = async (categoryId) => {
    if (!categoryId || !user) return;
    setLoading(true);
    try {
      const baseUrl = user.authorities.includes("ROLE_HR")
        ? process.env.REACT_APP_API_HR_URL
        : process.env.REACT_APP_API_BASE_URL;

      const [eventsRes, cronjobsRes] = await Promise.all([
        axiosInstance.get(`${baseUrl}/scheduled-events/category/${categoryId}`),
        axiosInstance.get(
          `${baseUrl}/scheduled-cron-jobs/category/${categoryId}`,
        ),
      ]);

      setEventTypes(eventsRes.data?.data || []);
      setCronjobTypes(cronjobsRes.data?.data || []);
    } catch (err) {
      console.error("Error fetching category details:", err);
      setEventTypes([]);
      setCronjobTypes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const selectCategoryFromNavbar = (event) => {
      const category = categories.find(
        (item) => item.categoryId === event.detail,
      );
      if (!category) return;
      setSelectedCategory(category);
      fetchCategoryDetails(category.categoryId);
    };
    window.addEventListener(
      "email-automation-category-select",
      selectCategoryFromNavbar,
    );
    return () =>
      window.removeEventListener(
        "email-automation-category-select",
        selectCategoryFromNavbar,
      );
  }, [categories]);

  const handleDelete = async (id, type) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        const isAdmin = user.authorities.includes("ROLE_ADMIN");
        let endpoint = "";
        if (type === "event") {
          endpoint = isAdmin
            ? `${process.env.REACT_APP_API_BASE_URL}/scheduled-events/${id}`
            : `${process.env.REACT_APP_API_HR_URL}/scheduled-events/${id}`;
          await axiosInstance.delete(endpoint);
          setEventTypes((prev) =>
            prev.filter((event) => event.scheduledEventId !== id),
          );
        } else if (type === "cronjob") {
          endpoint = isAdmin
            ? `${process.env.REACT_APP_API_BASE_URL}/scheduled-cron-jobs/${id}`
            : `${process.env.REACT_APP_API_HR_URL}/scheduled-cron-jobs/${id}`;
          await axiosInstance.delete(endpoint);
          setCronjobTypes((prev) =>
            prev.filter((cronjob) => cronjob.scheduledCronJobId !== id),
          );
        }
      } catch (err) {
        console.error("Error deleting item:", err);
      }
    }
  };

  const handleToggle = async (id, type, currentStatus) => {
    try {
      const isAdmin = user.authorities.includes("ROLE_ADMIN");
      const action =
        currentStatus.toUpperCase() === "ENABLED" ? "disable" : "enable";
      const endpointType = type === "event" ? "events" : "cron-jobs";

      const endpoint = isAdmin
        ? `${process.env.REACT_APP_API_BASE_URL}/scheduled-${endpointType}/${action}/${id}`
        : `${process.env.REACT_APP_API_HR_URL}/scheduled-${endpointType}/${action}/${id}`;

      await axiosInstance.put(endpoint);

      const newStatus =
        currentStatus.toUpperCase() === "ENABLED" ? "DISABLED" : "ENABLED";
      if (type === "cron-job") {
        setCronjobTypes((prev) =>
          prev.map((item) =>
            item.scheduledCronJobId === id
              ? { ...item, status: newStatus }
              : item,
          ),
        );
      } else {
        setEventTypes((prev) =>
          prev.map((item) =>
            item.scheduledEventId === id
              ? { ...item, status: newStatus }
              : item,
          ),
        );
      }
    } catch (err) {
      console.error("Error toggling status:", err);
    }
  };

  const handleCloseCronJobForm = () => {
    setIsCronJobFormOpen(false);
    setEditingCronJobId(null);
    if (selectedCategory?.categoryId) {
      fetchCategoryDetails(selectedCategory.categoryId);
    }
  };

  const handleCloseEventForm = () => {
    setIsEventJobFormOpen(false);
    setEventJobId(null);
    if (selectedCategory?.categoryId) {
      fetchCategoryDetails(selectedCategory.categoryId);
    }
  };

  const renderNotificationBadges = (notificationTypes) => {
    if (!notificationTypes || notificationTypes.length === 0) return "N/A";
    return (
      <div className="notification-badges">
        {notificationTypes.map((notif, index) => (
          <span
            key={index}
            className={`notification-badge ${notif.notificationType.toLowerCase()}`}
          >
            {notif.notificationType.replace("_", "-")}
          </span>
        ))}
      </div>
    );
  };

  const renderActionButtons = (item, type) => (
    <div className="action-buttons">
      <IconButton
        icon="iconamoon:edit-light"
        variant="edit"
        onClick={() => {
          if (type === "event") {
            setEventJobId(item.scheduledEventId);
            setIsEventJobFormOpen(true);
          } else {
            setEditingCronJobId(item.scheduledCronJobId);
            setIsCronJobFormOpen(true);
          }
        }}
        className="edit-button"
      />
      <IconButton
        icon="fluent:delete-28-regular"
        variant="delete"
        onClick={() =>
          handleDelete(
            type === "event" ? item.scheduledEventId : item.scheduledCronJobId,
            type,
          )
        }
        className="delete-button"
      />
      <ToggleButton
        isOn={item.status.toUpperCase() === "ENABLED"}
        onToggle={() =>
          handleToggle(
            type === "event" ? item.scheduledEventId : item.scheduledCronJobId,
            type,
            item.status,
          )
        }
      />
    </div>
  );

  const eventColumns = [
    { header: "Task Name", accessor: "taskName" },
    { header: "Notification Type", accessor: "eventType" },
    { header: "Trigger Types", accessor: "notificationTypes" },
    { header: "From Email Source", accessor: "fromEmailSource" },
    { header: "To Email Source", accessor: "toEmailSourceFrom" },
    { header: "From Email", accessor: "fromEmail" },
    { header: "Actions", accessor: "actions" },
  ];

  const cronjobColumns = [
    { header: "Task Name", accessor: "taskName" },
    { header: "Automations Type", accessor: "cronJobType" },
    { header: "Notification Types", accessor: "notificationTypes" },
    { header: "From Email", accessor: "fromEmailSource" },
    { header: "To Email", accessor: "toEmailSourceFrom" },
    { header: "Time", accessor: "formattedTime" },
    { header: "Last Notified At", accessor: "lastNotifiedAt" },
    { header: "Trigger Date", accessor: "triggerDate" },
    { header: "Days", accessor: "days" },
    { header: "Actions", accessor: "actions" },
  ];

  const transformedEventData = eventTypes.map((event) => ({
    ...event,
    notificationTypes: renderNotificationBadges(event.notificationTypes),
    actions: renderActionButtons(event, "event"),
  }));

  const transformedCronjobData = cronjobTypes.map((cronjob) => {
    const [_, minute, hour] = cronjob.cronJobExpression.split(" ");
    const formattedTime = new Date();
    formattedTime.setHours(parseInt(hour, 10), parseInt(minute, 10), 0);
    return {
      ...cronjob,
      notificationTypes: renderNotificationBadges(cronjob.notificationTypes),
      formattedTime: formattedTime.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
      lastNotifiedAt: cronjob.lastNotifiedAt
        ? new Date(cronjob.lastNotifiedAt).toLocaleString()
        : "N/A",
      actions: renderActionButtons(cronjob, "cronjob"),
    };
  });

  if (isAuthLoading) {
    return <LoadingScreen />;
  }

  // If user doesn't have access to Email Automation, show upgrade plan
  if (!hasEmailAutomationAccess()) {
    return (
      <UpgradePlan
        featureName="Email Automation"
        description="Access advanced email automation features including scheduled events, cron jobs, and notification management"
      />
    );
  }

  return (
    <div className="email-automation-container">
      {ifHeading && <h1 className="display6">No Email Automation Found</h1>}
      {isCronJobFormOpen ? (
        <CronJobForm
          categoryId={selectedCategory?.categoryId}
          scheduledCronJobId={editingCronJobId}
          onClose={handleCloseCronJobForm}
        />
      ) : isEventJobFormOpen ? (
        <EventForm
          categoryId={selectedCategory?.categoryId}
          scheduledEventId={eventJobId}
          onClose={handleCloseEventForm}
        />
      ) : (
        <>
          {selectedCategory && (
            <div className="category-details">
              <div className="tables-container">
                <div className="table-wrapper">
                  <div className="table-type">
                    <button
                      className="top-right-button"
                      onClick={() => setIsEventJobFormOpen(true)}
                    >
                      Create Notification
                    </button>
                  </div>
                  <GeneralTable
                    columns={eventColumns}
                    data={transformedEventData}
                    title="Scheduled Events"
                  />
                </div>
                <div className="table-wrapper">
                  <div className="table-type">
                    <button
                      className="top-right-button"
                      onClick={() => setIsCronJobFormOpen(true)}
                    >
                      Create Automation
                    </button>
                  </div>
                  <GeneralTable
                    columns={cronjobColumns}
                    data={transformedCronjobData}
                    title="Automations"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmailAutomation;
